import { useMutation, useQuery } from '@apollo/client';
import { FileCopyRounded } from '@mui/icons-material';
import { Alert } from '@mui/material';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import { FormSchemaProvider, FormSchemaProviderProps } from '@procureai/ui-form-mui';
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetIcon,
  useLanguageCode,
  WidgetEditActions,
  useUuid,
  useScrollIntoView,
  useCustomSnackbar,
  CurrencyProvider,
} from '@procureai/ui-mui';
import { isNotNil } from 'ramda';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNegotiationDetail } from '../Module.hooks';
import {
  NegotiationQuoteDetailsQuery,
  TsrctNegotiationCaseStatus,
  TsrctQuoteItemStatus,
  TsrctQuoteMode,
} from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { WidgetContentEmpty } from '~/components/WidgetContentEmpty/WidgetContentEmpty';
import { useHashActionEdit } from '~/hooks/useHashActionEdit/useHashActionEdit';
import { ModuleHash } from '~/modules/Module.types';

import { QuoteContextProvider, QuoteContextProviderValue } from './components/QuoteContextProvider';
import { QuoteDetails } from './components/QuoteDetails/QuoteDetails';
import { QuoteDetailsForm } from './components/QuoteDetailsForm/QuoteDetailsForm';
import {
  QuoteFormData,
  quoteFormDataDefault,
  quoteFormDataSchema,
  TsrctQuoteMapper,
} from './components/QuoteDetailsForm/QuoteDetailsForm.schema';
import { NEGOTIATION_QUOTE_DETAILS_UPDATE_MUTATION } from './NegotiationQuoteDetailsWidget.mutation.gql';
import { NEGOTIATION_QUOTE_DETAILS_QUERY } from './NegotiationQuoteDetailsWidget.query.gql';
import { Quote } from './NegotiationQuoteDetailsWidget.types';
import { mutationDataFromQuoteForm } from './NegotiationQuoteDetailsWidget.util';

const isQuoteEditable = (data: NegotiationQuoteDetailsQuery | undefined) => {
  return data?.tesseract.negotiationCase.status === TsrctNegotiationCaseStatus.INITIALIZED;
};

export interface NegotiationQuoteDetailsWidgetProps {
  // widget is part of a bigger form -> do not add own form and saving capabilities
  hasParentForm?: boolean;
}

export const NegotiationQuoteDetailsWidget = ({ hasParentForm }: NegotiationQuoteDetailsWidgetProps) => {
  const { negotiationId } = useNegotiationDetail();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiationQuoteDetailsWidget' });
  const [widgetRef, active, hashAction] = useScrollIntoView({ hash: ModuleHash.QUOTE });
  const { onError, enqueueSuccessSnackbar } = useCustomSnackbar();
  const formId = useUuid();
  const { languageCode } = useLanguageCode();

  const variables = {
    id: negotiationId,
    language: languageCode,
  };

  const { data, loading, error } = useQuery(NEGOTIATION_QUOTE_DETAILS_QUERY, {
    variables,
    skip: !negotiationId,
  });

  const [quoteUpdate, { loading: saving }] = useMutation(NEGOTIATION_QUOTE_DETAILS_UPDATE_MUTATION, {
    refetchQueries: [{ query: NEGOTIATION_QUOTE_DETAILS_QUERY, variables }],
    onError,
  });

  const [editOpen, setEditOpen] = useState(hasParentForm);
  const contentData = hasParentForm ? quoteFormDataDefault : data?.tesseract.negotiationCase;
  // TODO: Need to review typing here, probably how the entire form is used
  const quote = contentData?.quote as Quote;
  const isMasked = data?.tesseract.negotiationCase.isMaskedBidding;
  const quoteMode = TsrctQuoteMode.EDIT;
  const contextValue = useMemo<QuoteContextProviderValue>(() => ({ quoteMode, isMasked }), [quoteMode, isMasked]);

  const handleEditToggle = useCallback(() => {
    setEditOpen((prev) => !prev);
  }, []);

  const handleEditApply: FormSchemaProviderProps<QuoteFormData>['onSubmit'] = (submitData) => {
    const quote = submitData?.quote;
    if (quote) {
      (async () => {
        const { errors } = await quoteUpdate({
          variables: {
            negotiationCaseId: negotiationId,
            data: mutationDataFromQuoteForm({ quote, quoteMode }),
          },
        });
        if (!errors) {
          setEditOpen(false);
          enqueueSuccessSnackbar(t('saveSuccess'));
        }
      })();
    }
  };

  const editDisabled = !isQuoteEditable(data);

  useHashActionEdit({
    id: data?.tesseract.negotiationCase?.id,
    active,
    editDisabled,
    editOpen,
    setEditOpen,
    hashAction,
  });

  // Transform TesseractQuote input into QuoteFormData or using default values
  const quoteFormData = useMemo(() => {
    return quote ? TsrctQuoteMapper.parse({ quote }) : quoteFormDataDefault;
  }, [quote]);
  let content: React.ReactNode;
  if (!error) {
    if (editOpen && contentData) {
      content = (
        <QuoteContextProvider value={contextValue}>
          <QuoteDetailsForm />
        </QuoteContextProvider>
      );

      const schema = quoteFormDataSchema(data?.tesseract.negotiationCase.options?.quoteMode);
      if (!hasParentForm) {
        content = (
          <FormSchemaProvider<QuoteFormData>
            id={formId}
            data={quoteFormData}
            schema={schema}
            defaultValues={quoteFormDataDefault}
            onSubmit={handleEditApply}
          >
            {content}
          </FormSchemaProvider>
        );
      }
    } else if (loading || quote) {
      content = (
        <CurrencyProvider value={quote?.currency ?? DEFAULT_CURRENCY}>
          <QuoteDetails isMasked={isMasked} data={quote} loading={loading} />
        </CurrencyProvider>
      );
    } else {
      content = <WidgetContentEmpty title={t('empty')} />;
    }
  } else {
    content = <WidgetContentEmpty title={t('error')} color="error.main" />;
  }
  const disabledCount = useMemo(
    () =>
      contentData?.quote?.lineItems?.filter((item) => item?.status === TsrctQuoteItemStatus.NOT_REQUESTED)?.length ?? 0,
    [contentData?.quote?.lineItems],
  );
  return (
    <Widget
      ref={widgetRef}
      active={active}
      hover={false}
      data-intercom-target="NegotiationQuoteDetailsWidget"
      data-testid="NegotiationQuoteDetailsWidget/Button/Edit"
    >
      <WidgetHeader
        title={t('title')}
        icon={<WidgetIcon icon={<FileCopyRounded fontSize="small" />} />}
        actionsRight={
          hasParentForm ? null : (
            <WidgetEditActions
              formId={formId}
              open={editOpen}
              saving={saving}
              disabled={editDisabled}
              onToggle={handleEditToggle}
            />
          )
        }
      />
      <WidgetContent>
        {isNotNil(disabledCount) && disabledCount > 0 && (
          <Alert severity="info">{t('label.disabledLineItems', { count: disabledCount })}</Alert>
        )}
        <div>{content}</div>
      </WidgetContent>
    </Widget>
  );
};
